import React, { useRef, useState } from "react";
import { gsap, useGSAP, EASE, prefersReducedMotion } from "../../lib/gsap";
import { experience as roles } from "../../data/portfolio";
import Reveal from "../motion/Reveal";
import SplitLines from "../motion/SplitLines";
import AxisReveal from "../motion/AxisReveal";
import Decode from "../motion/Decode";
import Ruler from "../motion/Ruler";
import Icon from "../ui/Icon";

/* ── One ledger entry, openable ─────────────────────────────────────── */
const Entry = ({ role, position, open, onToggle }) => {
  const body = useRef(null);
  const panelId = `log-panel-${role.id}`;

  useGSAP(
    () => {
      const el = body.current;
      if (!el) return undefined;

      if (prefersReducedMotion()) {
        gsap.set(el, { height: open ? "auto" : 0, opacity: open ? 1 : 0 });
        return undefined;
      }

      const tween = gsap.to(el, {
        height: open ? "auto" : 0,
        opacity: open ? 1 : 0,
        duration: open ? 0.55 : 0.4,
        ease: open ? EASE.out : "power2.in",
      });

      return () => tween.kill();
    },
    { dependencies: [open] }
  );

  return (
    <li className={position === 0 ? "" : "hair-t"}>
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          className="group grid w-full grid-cols-12 items-baseline gap-x-4 gap-y-2 py-7 text-left md:py-9"
          data-cursor-label={open ? "Close" : "Open"}
        >
          <span className="col-span-12 flex items-baseline gap-3 md:col-span-3">
            <span className="t-num text-xs text-flare">
              {String(position + 1).padStart(2, "0")}
            </span>
            <span className="t-mono text-dim">{role.period}</span>
          </span>

          <span className="col-span-11 md:col-span-6">
            <span
              className="t-h3 block text-fg transition-[font-variation-settings] duration-500 ease-out"
              style={{
                fontVariationSettings: open
                  ? "'wdth' 116, 'wght' 700"
                  : "'wdth' 100, 'wght' 600",
              }}
            >
              {role.company}
            </span>
            <span className="mt-1 block text-sm text-dim">{role.title}</span>
          </span>

          <span className="col-span-1 flex items-center justify-end md:col-span-3">
            <Icon name={open ? "minus" : "plus"} size={18} />
          </span>
        </button>
      </h3>

      <div
        id={panelId}
        ref={body}
        className="grid-12 gap-y-6 overflow-hidden"
        style={{ height: 0, opacity: 0 }}
        role="region"
      >
        <div className="col-span-12 pb-9 md:col-span-6 md:col-start-4">
          <SplitLines as="p" className="t-body" stagger={0.05} play={open}>
            {role.description}
          </SplitLines>
        </div>

        <ul className="col-span-12 flex flex-col gap-3 pb-9 md:col-span-3 md:col-start-10">
          {role.achievements.map((achievement) => (
            <li key={achievement} className="flex gap-3">
              <span className="reg-mark mt-1.5 shrink-0" aria-hidden="true" />
              <span className="text-[0.875rem] leading-relaxed text-dim">{achievement}</span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

/**
 * Experience as a ledger you operate, not a timeline you scroll past: entries
 * are collapsed to period + company, and open on demand. One hairline's fill is
 * scrubbed by scroll position so progress through the log stays legible.
 */
const Experience = () => {
  const scope = useRef(null);
  const [openId, setOpenId] = useState(roles[0]?.id ?? null);

  useGSAP(
    () => {
      if (prefersReducedMotion()) return undefined;

      const fill = scope.current.querySelector("[data-log-fill]");
      const track = scope.current.querySelector("[data-log-track]");
      if (!fill || !track) return undefined;

      const tween = gsap.fromTo(
        fill,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: { trigger: track, start: "top 70%", end: "bottom 70%", scrub: 0.4 },
        }
      );

      return () => tween.scrollTrigger?.kill();
    },
    { scope }
  );

  return (
    <section id="experience" ref={scope} className="chapter chapter-pad relative" aria-labelledby="experience-title">
      <div className="page">
        <div className="flex flex-wrap items-baseline justify-between gap-4 pb-4">
          <h2 id="experience-title" className="t-label">
            <span className="text-flare">05</span> — Log
            <span className="sr-only"> — experience</span>
          </h2>
          <Decode
            as="span"
            className="t-label"
            text={`${String(roles.length).padStart(2, "0")} entries · select to open`}
          />
        </div>
        <Ruler ticks={16} />

        <Reveal className="mt-10 max-w-measure">
          <AxisReveal as="p" className="t-lead text-dim" wdth={100} wght={400} fromWdth={78}>
            Four positions, all current or recent — open one to read what the work actually was.
          </AxisReveal>
        </Reveal>

        <div data-log-track className="relative mt-10 md:mt-14">
          {/* Progress rule */}
          <div className="absolute left-0 top-0 hidden h-full w-px bg-line md:block" aria-hidden="true">
            <div
              data-log-fill
              className="h-full w-px origin-top"
              style={{ background: "var(--flare)", transform: "scaleY(0)" }}
            />
          </div>

          <ol className="md:pl-8 lg:pl-12">
            {roles.map((role, i) => (
              <Entry
                key={role.id}
                role={role}
                position={i}
                open={openId === role.id}
                onToggle={() => setOpenId((prev) => (prev === role.id ? null : role.id))}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default Experience;
